<template>
  <div class="page-view">

    <!-- Header -->
    <BasePageHeader
      :title="isNew ? $t('itemView.newItemType') : (form.name || $t('itemView.newItemType'))"
      @back="$router.back()"
    >
      <template #actions>
        <router-link
          v-if="isWarehouseGlance"
          class="btn ghost small"
          :to="{ path: route.path, query: { ...route.query, edit: 'full' } }"
        >{{ $t('ux.glanceEditFull') }}</router-link>
        <router-link
          v-else-if="!isNew && shell.isWarehouse"
          class="btn ghost small"
          :to="{ path: route.path, query: { ...route.query, edit: undefined } }"
        >{{ $t('ux.glanceOps') }}</router-link>
        <template v-if="isOperatorOrAbove && showFullEditor">
          <template v-if="isNew">
            <button class="btn ghost small" :disabled="saving || !formValid" @click="save(true)">
              {{ saving ? $t('common.saving') : $t('itemView.createAddNew') }}
            </button>
            <button class="btn primary small" :disabled="saving || !formValid" @click="save()">
              {{ saving ? $t('common.saving') : $t('common.create') }}
            </button>
          </template>
          <template v-else>
            <button class="btn ghost small" :disabled="saving || !formValid" @click="save(false, true)">
              {{ saving ? $t('common.saving') : $t('itemView.saveClose') }}
            </button>
            <button class="btn primary small" :disabled="saving || !formValid" @click="save()">
              {{ saving ? $t('common.saving') : $t('common.save') }}
            </button>
          </template>
        </template>
      </template>
    </BasePageHeader>

    <EmptyState v-if="loading" :message="$t('common.loading')" />
    <EmptyState v-else-if="loadError" variant="error" :message="loadError" />

    <div v-else class="mx-auto w-full max-w-5xl px-3 py-4 sm:px-6 space-y-5">

      <!-- ── Location summary ─────────────────────────────────────── -->
      <div v-if="!isNew && locationSummary.length" class="flex items-center flex-wrap gap-1.5 px-4 py-2.5 border-b border-gray-800 text-teal-400">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="shrink-0"><path d="M21 10c0 6-9 13-9 13S3 16 3 10a9 9 0 0 1 18 0Z"/><circle cx="12" cy="10" r="3"/></svg>
        <span v-for="loc in locationSummary" :key="loc.key" class="text-xs font-medium px-2 py-0.5 bg-[#1c2a2a] border border-[#0f3d3d] rounded text-teal-400">
          <span v-if="loc.locationName || loc.warehouseName" class="text-[#2d9c8e] font-normal">{{ [loc.locationName, loc.warehouseName].filter(Boolean).join(' › ') }} › </span>{{ loc.areaName }}
        </span>
      </div>

      <!-- ── Location filter warning ────────────────────────────────── -->
      <div v-if="notAtCurrentFilter" class="flex items-center gap-2 px-4 py-2.5 border-b border-amber-800/50 bg-amber-950/40 text-amber-400">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="shrink-0"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        <span class="text-xs">{{ $t('itemView.notAtFilterMsg', { name: notAtCurrentFilterLabel }) }}</span>
      </div>

      <!-- ── Status Hero (types 1, 3, 4) ──────────────────────────────── -->
      <div v-if="!isNew && (form.item_type === 1 || form.item_type === 3 || form.item_type === 4) && uniqueInstId" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <span v-if="uniqueInst.in_downtime" class="badge badge-warning badge-pill text-xs mb-3">{{ $t('itemView.inDowntimeLabel') }}</span>
        <template v-if="activeCheckouts.length">
          <div
            v-for="co in activeCheckouts"
            :key="co.id"
            class="flex flex-col gap-1 px-3.5 py-3 rounded-xl border mb-2"
            :class="!co.effective_expected_checkin ? 'bg-amber-950/50 border-amber-700/50' : 'bg-[var(--color-surface)] border-gray-700'"
          >
            <div class="flex items-center gap-2 flex-wrap">
              <MdiAccountOutline class="w-4 h-4 text-gray-400 shrink-0" />
              <span class="text-sm text-gray-200 font-medium">{{ co.user || $t('itemView.noUserAssigned') }}</span>
              <span v-if="co.project" class="text-xs text-gray-400">
                · <MdiFolderOutline class="w-3.5 h-3.5 inline -mt-0.5 text-gray-500" /> {{ co.project }}
              </span>
            </div>
            <div class="flex items-center gap-2 text-xs mt-0.5">
              <MdiCalendarOutline class="w-3.5 h-3.5 text-gray-500 shrink-0" />
              <span class="text-gray-400">{{ $t('itemView.checkedOutOn', { date: fmtDatetime(co.checkout_date) }) }}</span>
            </div>
            <div class="flex items-center gap-2 text-xs mt-0.5">
              <MdiCalendarArrowRight class="w-3.5 h-3.5 shrink-0" :class="!co.effective_expected_checkin ? 'text-amber-500' : 'text-gray-500'" />
              <span v-if="co.effective_expected_checkin" class="text-gray-400">{{ $t('ui.expectedReturn') }} {{ fmtDatetime(co.effective_expected_checkin) }}</span>
              <span v-else class="text-amber-400 font-semibold">{{ $t('itemView.noReturnDate') }}</span>
            </div>
          </div>
        </template>
        <div v-else-if="uniqueInst.status === 1 && !uniqueInst.in_downtime" class="text-sm text-emerald-400 flex items-center gap-2">
          <MdiCheckCircleOutline class="w-4 h-4" />
          {{ $t('itemView.inServiceMsg') }}
        </div>
        <div v-else-if="uniqueInst.status === 2" class="text-sm text-red-400 flex items-center gap-2">
          <MdiAlertCircleOutline class="w-4 h-4" />
          {{ $t('itemView.defectiveMsg') }}
        </div>
        <div v-else-if="uniqueInst.status === 3" class="text-sm text-gray-500 flex items-center gap-2">
          <MdiArchiveOutline class="w-4 h-4" />
          {{ $t('itemView.retiredMsg') }}
        </div>
      </div>

      <!-- ── Contained in (parent link) ────────────────────────────── -->
      <div v-if="parentInstance && (form.item_type === 1 || form.item_type === 4)" class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('itemView.containedIn') }}</div>
        <div
          class="flex items-center gap-2 px-3.5 py-2.5 bg-[var(--color-surface)] border border-gray-800 rounded-xl cursor-pointer transition-colors hover:bg-gray-800"
          @click="goToParent"
        >
          <MdiPackageVariantClosed class="w-4 h-4 text-teal-400 shrink-0" />
          <div class="flex-1">
            <div class="text-sm font-semibold">{{ parentInstance.item?.name ?? $t('itemMetadata.container') }}</div>
            <div class="text-xs text-gray-500">
              {{ parentInstance.assetTag || parentInstance.serial_number || '#' + parentInstance.id }}
            </div>
          </div>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 text-gray-600 shrink-0">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </div>
      </div>

      <!-- ── Item type details ─────────────────────────────────────── -->
      <BaseEditorSection :title="$t('itemView.itemTypeDetails')">


        <div class="grid grid-cols-1 gap-x-5 sm:grid-cols-2">
        <BaseFormField :label="$t('common.name')" required>
          <BaseInput v-model="form.name" :placeholder="$t('ui.eGCanonXf300Camera')" maxlength="200" />
        </BaseFormField>

        <BaseFormField>
          <div class="flex items-center justify-start mb-2">
            <label class="form-label mb-0">{{ $t('itemView.category') }} <span class="form-required">*</span></label>
            <button
              type="button"
              @click="toggleNewCategoryForm"
              class="btn primary small ml-6">
              {{ showNewCategoryForm ? $t('itemView.cancelNewCategory') : $t('itemView.newCategory') }}
            </button>
          </div>
          <BaseSelect v-model="form.category">
            <option value="" disabled>{{ $t('itemView.selectCategory') }}</option>
            <option v-for="cat in categories" :key="cat.id" :value="cat.id">{{ cat.name }}</option>
          </BaseSelect>

          <div v-if="showNewCategoryForm" class="mt-3 p-4 bg-[var(--color-surface)] border border-blue-900 rounded-xl">
            <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('itemView.newCategoryTitle') }}</div>
            <BaseInput
              v-model="newCategoryForm.name"
              :placeholder="$t('itemView.categoryNamePlaceholder')"
              class="mb-3"
            />
            <BaseSelect
              v-model="newCategoryForm.parent"
              class="mb-3">
              <option :value="null">{{ $t('itemView.noParentCategory') }}</option>
              <option v-for="c in rootCategories" :key="c.id" :value="c.id">{{ c.name }}</option>
            </BaseSelect>
            <div class="flex gap-2 justify-end">
              <button
                type="button"
                @click="toggleNewCategoryForm"
                class="btn ghost small">
                {{ $t('common.cancel') }}
              </button>
              <button
                type="button"
                @click="saveNewCategory"
                :disabled="savingCategory || !newCategoryForm.name"
                class="btn primary small">
                {{ savingCategory ? $t('common.saving') : $t('common.create') }}
              </button>
            </div>
          </div>
        </BaseFormField>

        </div>

        <BaseFormField :label="$t('common.type')" required>
          <BaseSelect
            :model-value="form.item_type"
            :disabled="typeChangeLocked"
            @update:model-value="(v) => form.item_type = v === '' ? '' : Number(v)"
          >
            <option value="" disabled>{{ $t('itemView.selectType') }}</option>
            <option :value="1">{{ $t('itemView.typeDesc.unique') }}</option>
            <option :value="2">{{ $t('itemView.typeDesc.multi') }}</option>
            <option :value="3">{{ $t('itemView.typeDesc.quantity') }}</option>
            <option :value="4">{{ $t('itemView.typeDesc.consumable') }}</option>
          </BaseSelect>
          <div v-if="typeChangeLocked" class="form-hint">{{ $t('itemView.typeChangeLocked') }}</div>
          <div v-else-if="form.item_type" class="form-hint">{{ itemTypeHint }}</div>
        </BaseFormField>

        <BaseFormField v-if="form.item_type === 1 || form.item_type === 2" :hint="$t('itemView.isContainerHint')">
          <BaseCheckbox v-model="form.is_container" :label="$t('itemView.isContainer')" />
        </BaseFormField>

        <BaseFormField :label="$t('common.description')">
          <BaseTextarea v-model="form.description" :placeholder="$t('itemView.descriptionPlaceholder')" :rows="3" />
        </BaseFormField>

      </BaseEditorSection>

      <!-- ── Unique / Quantity: inline instance form ─────────────── -->
      <BaseEditorSection v-if="form.item_type === 1 || form.item_type === 3" :title="$t('itemView.unitDetails')">

        <div class="form-hint mb-3.5 -mt-2">
          {{ isNew ? $t('itemView.firstInstanceHint') : $t('itemView.savedWithItem') }}
        </div>

        <div v-if="form.item_type !== 3" class="form-row">
          <BaseFormField :label="$t('itemView.assetTag')">
            <BaseInput v-model="uniqueInst.assetTag" placeholder="ABC-001" maxlength="200" />
          </BaseFormField>
          <BaseFormField :label="$t('itemView.serialNumber')">
            <BaseInput v-model="uniqueInst.serial_number" placeholder="SN12345" maxlength="200" />
          </BaseFormField>
        </div>

        <BaseFormField>
          <div class="flex items-center justify-start mb-2">
            <label class="form-label mb-0">{{ $t('itemView.area') }} <span class="form-required">*</span></label>
            <button
              type="button"
              @click="toggleNewAreaForm"
              class="btn primary small ml-6">
              {{ showNewAreaForm ? $t('itemView.cancelNewArea') : $t('itemView.newArea') }}
            </button>
          </div>
          <BaseSelect v-model="uniqueInst.area">
            <option value="" disabled>{{ $t('itemView.selectArea') }}</option>
            <option v-for="a in areas" :key="a.id" :value="a.id">{{ a.name }}</option>
          </BaseSelect>

          <div v-if="showNewAreaForm" class="mt-3 p-4 bg-[var(--color-surface)] border border-blue-900 rounded-xl">
            <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('areas.new') }}</div>
            <BaseFormField :label="$t('common.name')" required>
              <BaseInput
                v-model="newAreaForm.name"
                :placeholder="$t('itemView.areaNamePlaceholder')"
              />
            </BaseFormField>
            <BaseFormField :label="$t('common.location')">
              <BaseSelect v-model="newAreaForm.location">
                <option value="">{{ $t('common.noLocation') }}</option>
                <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
              </BaseSelect>
            </BaseFormField>
            <BaseFormField :label="$t('entity.warehouse')">
              <BaseSelect v-model="newAreaForm.warehouse">
                <option value="">{{ $t('common.noWarehouse') }}</option>
                <option v-for="wh in newAreaFormWarehouses" :key="wh.id" :value="wh.id">{{ wh.name }}</option>
              </BaseSelect>
            </BaseFormField>
            <div class="flex gap-2 justify-end">
              <button 
                type="button"
                @click="toggleNewAreaForm" 
                class="btn ghost small">
                {{ $t('common.cancel') }}
              </button>
              <button 
                type="button"
                @click="saveNewArea" 
                :disabled="savingArea || !newAreaForm.name"
                class="btn primary small">
                {{ savingArea ? $t('common.saving') : $t('common.create') }}
              </button>
            </div>
          </div>
        </BaseFormField>

        <BaseFormField :label="$t('common.status')">
          <BaseSelect
            :model-value="uniqueInst.status"
            @update:model-value="(v) => uniqueInst.status = Number(v)"
          >
            <option :value="1">{{ $t('instanceStatus.1') }}</option>
            <option :value="2">{{ $t('instanceStatus.2') }}</option>
            <option :value="3">{{ $t('instanceStatus.3') }}</option>
          </BaseSelect>
        </BaseFormField>

        <BaseFormField :label="$t('itemView.comment')">
          <BaseInput v-model="uniqueInst.comment" :placeholder="$t('itemView.commentPlaceholder')" />
        </BaseFormField>

        <BaseFormField v-if="form.item_type === 3" :label="$t('itemView.totalQuantity')">
          <BaseInput type="number" min="0" v-model="uniqueInst.available_quantity" placeholder="0" />
        </BaseFormField>

        <!-- ── Maintenance quick actions (type 1 / 3) ─────────────── -->
        <div v-if="!isNew && uniqueInstId" class="flex items-center gap-3 mt-3.5 flex-wrap">
          <button
            v-if="!uniqueInst.in_downtime"
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-amber-900 text-amber-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-800"
            :disabled="togglingDowntime"
            @click="openMarkMaintenance(uniqueInstId)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
            {{ $t('itemView.markMaintenanceBtn') }}
          </button>
          <button
            v-else
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-green-900 text-green-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-800"
            :disabled="togglingDowntime"
            @click="openReturnService(uniqueInstId)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
            {{ $t('itemView.returnToServiceBtn') }}
          </button>
          <span v-if="uniqueInst.in_downtime && uniqueInst.downtime_reason" class="text-xs text-amber-300 opacity-75 italic">{{ uniqueInst.downtime_reason }}</span>
          <router-link
            :to="`/maintenance/schedules?instance=${uniqueInstId}`"
            class="text-xs text-blue-400 no-underline whitespace-nowrap hover:underline"
          >{{ $t('itemView.viewSchedulesLink') }}</router-link>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-blue-900 text-blue-300 transition-colors hover:bg-blue-800"
            @click="openAddScheduleModal"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
            {{ $t('ui.addToSchedule') }}
          </button>
        </div>
      </BaseEditorSection>

      <!-- ── Consumable: stock settings ───────────────────────────── -->
      <BaseEditorSection v-if="form.item_type === 4" :title="$t('itemView.stockSettings')">

        <div class="form-hint mb-3.5 -mt-2">
          {{ isNew ? $t('itemView.stockHintNew') : $t('itemView.stockHintEdit') }}
        </div>

        <BaseFormField>
          <div class="flex items-center justify-start mb-2">
            <label class="form-label mb-0">{{ $t('common.area') }} <span class="form-required">*</span></label>
            <button
              type="button"
              @click="toggleNewAreaForm"
              class="btn primary small ml-6">
              {{ showNewAreaForm ? $t('itemView.cancelNewArea') : $t('itemView.newArea') }}
            </button>
          </div>
          <BaseSelect v-model="uniqueInst.area">
            <option value="" disabled>{{ $t('itemView.selectStorageArea') }}</option>
            <option v-for="a in areas" :key="a.id" :value="a.id">{{ a.name }}</option>
          </BaseSelect>

          <div v-if="showNewAreaForm" class="mt-3 p-4 bg-[var(--color-surface)] border border-blue-900 rounded-xl">
            <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('areas.new') }}</div>
            <BaseFormField :label="$t('common.name')" required>
              <BaseInput v-model="newAreaForm.name" :placeholder="$t('itemView.areaNamePlaceholder')" />
            </BaseFormField>
            <BaseFormField :label="$t('common.location')">
              <BaseSelect v-model="newAreaForm.location">
                <option value="">{{ $t('common.noLocation') }}</option>
                <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
              </BaseSelect>
            </BaseFormField>
            <BaseFormField :label="$t('entity.warehouse')">
              <BaseSelect v-model="newAreaForm.warehouse">
                <option value="">{{ $t('common.noWarehouse') }}</option>
                <option v-for="wh in newAreaFormWarehouses" :key="wh.id" :value="wh.id">{{ wh.name }}</option>
              </BaseSelect>
            </BaseFormField>
            <div class="flex gap-2 justify-end">
              <button type="button" @click="toggleNewAreaForm" class="btn ghost small">{{ $t('common.cancel') }}</button>
              <button type="button" @click="saveNewArea" :disabled="savingArea || !newAreaForm.name" class="btn primary small">
                {{ savingArea ? $t('common.saving') : $t('common.create') }}
              </button>
            </div>
          </div>
        </BaseFormField>

        <BaseFormField :label="$t('itemView.stockQuantity')" required :hint="$t('itemView.stockQuantityHint')">
          <BaseInput type="number" min="0" v-model="uniqueInst.available_quantity" placeholder="0" />
        </BaseFormField>

        <div class="form-row">
          <BaseFormField :label="$t('itemView.restockThreshold')" :hint="$t('itemView.restockThresholdHint')">
            <BaseInput type="number" min="0" v-model="uniqueInst.restock_threshold" :placeholder="$t('ui.eG5')" />
          </BaseFormField>
          <BaseFormField :label="$t('itemView.suggestedRestockQty')" :hint="$t('itemView.suggestedRestockHint')">
            <BaseInput type="number" min="0" v-model="uniqueInst.restock_quantity" :placeholder="$t('ui.eG20')" />
          </BaseFormField>
        </div>

        <!-- Maintenance quick actions (type 4) -->
        <div v-if="!isNew && uniqueInstId" class="flex items-center gap-3 mt-3.5 flex-wrap">
          <button
            v-if="!uniqueInst.in_downtime"
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-amber-900 text-amber-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-amber-800"
            :disabled="togglingDowntime"
            @click="openMarkMaintenance(uniqueInstId)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
            {{ $t('itemView.markMaintenanceBtn') }}
          </button>
          <button
            v-else
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-green-900 text-green-300 transition-colors disabled:opacity-40 disabled:cursor-not-allowed hover:bg-green-800"
            :disabled="togglingDowntime"
            @click="openReturnService(uniqueInstId)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><polyline points="20 6 9 17 4 12"/></svg>
            {{ $t('itemView.returnToServiceBtn') }}
          </button>
          <span v-if="uniqueInst.in_downtime && uniqueInst.downtime_reason" class="text-xs text-amber-300 opacity-75 italic">{{ uniqueInst.downtime_reason }}</span>
          <router-link
            :to="`/maintenance/schedules?instance=${uniqueInstId}`"
            class="text-xs text-blue-400 no-underline whitespace-nowrap hover:underline"
          >{{ $t('itemView.viewSchedulesLink') }}</router-link>
          <button
            class="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-semibold cursor-pointer border-none bg-blue-900 text-blue-300 transition-colors hover:bg-blue-800"
            @click="openAddScheduleModal"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-4 h-4 shrink-0"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/><line x1="10" y1="16" x2="14" y2="16"/></svg>
            {{ $t('ui.addToSchedule') }}
          </button>
        </div>
      </BaseEditorSection>

      <!-- ── Multi: instances list + add ──────────────────────────── -->
      <div v-if="form.item_type === 2" class="px-4 pt-5 pb-2 border-b border-gray-800">        <div class="flex items-center justify-between mb-3.5">
          <div class="section-label" style="margin-bottom:0">{{ isNew ? $t('itemView.firstInstance') : $t('itemView.instances') }}</div>
          <button v-if="!isNew" class="btn ghost small" @click="toggleAddForm">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-3.5 h-3.5">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            {{ showAddForm ? $t('common.cancel') : $t('common.add') }}
          </button>
        </div>
        <div v-if="isNew" class="form-hint mb-3.5 -mt-2">{{ $t('itemView.firstInstanceHint') }}</div>

        <!-- Inline add form -->
        <div v-if="isNew || showAddForm" class="bg-[var(--color-surface)] border border-blue-900 rounded-xl p-4 mb-3">
          <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('itemView.newInstanceTitle') }}</div>

          <div class="form-row">
            <BaseFormField :label="$t('itemView.assetTag')">
              <BaseInput v-model="newInst.assetTag" placeholder="ABC-001" maxlength="200" />
            </BaseFormField>
            <BaseFormField :label="$t('itemView.serialNumber')">
              <BaseInput v-model="newInst.serial_number" placeholder="SN12345" maxlength="200" />
            </BaseFormField>
          </div>

          <BaseFormField>
            <div class="flex items-center justify-between mb-2">
              <label class="form-label mb-0">{{ $t('common.area') }} <span class="form-required">*</span></label>
              <button
                type="button"
                @click="toggleNewAreaForm"
                class="btn primary small">
                {{ showNewAreaForm ? $t('itemView.cancelNewArea') : $t('itemView.newArea') }}
              </button>
            </div>
            <BaseSelect v-model="newInst.area">
              <option value="" disabled>{{ $t('items.selectArea') }}</option>
              <option v-for="a in areas" :key="a.id" :value="a.id">{{ a.name }}</option>
            </BaseSelect>

            <div v-if="showNewAreaForm" class="mt-3 p-4 bg-[var(--color-surface)] border border-blue-900 rounded-xl">
              <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('areas.new') }}</div>
              <BaseFormField :label="$t('common.name')" required>
                <BaseInput
                  v-model="newAreaForm.name"
                  :placeholder="$t('itemView.areaNamePlaceholder')"
                />
              </BaseFormField>
              <BaseFormField :label="$t('common.location')">
                <BaseSelect v-model="newAreaForm.location">
                  <option value="">{{ $t('common.noLocation') }}</option>
                  <option v-for="loc in locations" :key="loc.id" :value="loc.id">{{ loc.name }}</option>
                </BaseSelect>
              </BaseFormField>
              <BaseFormField :label="$t('entity.warehouse')">
                <BaseSelect v-model="newAreaForm.warehouse">
                  <option value="">{{ $t('common.noWarehouse') }}</option>
                  <option v-for="wh in newAreaFormWarehouses" :key="wh.id" :value="wh.id">{{ wh.name }}</option>
                </BaseSelect>
              </BaseFormField>
              <div class="flex gap-2 justify-end">
                <button 
                  type="button"
                  @click="toggleNewAreaForm" 
                  class="btn ghost small">
                  {{ $t('common.cancel') }}
                </button>
                <button 
                  type="button"
                  @click="saveNewArea" 
                  :disabled="savingArea || !newAreaForm.name"
                  class="btn primary small">
                  {{ savingArea ? $t('common.saving') : $t('common.create') }}
                </button>
              </div>
            </div>
          </BaseFormField>

          <div class="form-row">
            <BaseFormField :label="$t('common.status')">
              <BaseSelect
                :model-value="newInst.status"
                @update:model-value="(v) => newInst.status = Number(v)"
              >
                <option :value="1">{{ $t('instanceStatus.1') }}</option>
                <option :value="2">{{ $t('instanceStatus.2') }}</option>
                <option :value="3">{{ $t('instanceStatus.3') }}</option>
              </BaseSelect>
            </BaseFormField>
            <BaseFormField v-if="form.item_type === 3" :label="$t('itemTypes.3')">
              <BaseInput type="number" min="0" v-model="newInst.available_quantity" placeholder="0" />
            </BaseFormField>
          </div>

          <BaseFormField :label="$t('itemView.comment')">
            <BaseInput v-model="newInst.comment" :placeholder="$t('itemView.commentPlaceholder')" />
          </BaseFormField>

          <BaseFormField :label="$t('itemView.purchasePrice')">
            <BaseInput type="number" min="0" step="0.01" v-model="newInst.purchase_price" placeholder="0.00" />
          </BaseFormField>

          <BaseFormField>
            <div class="flex items-center justify-start mb-2">
              <label class="form-label mb-0">{{ $t('assetValues.groupOptions.supplier') }}</label>
              <button type="button" @click="toggleNewSupplierForm" class="btn primary small ml-6">
                {{ showNewSupplierForm ? $t('itemView.cancelNewArea') : $t('itemView.newSupplier') }}
              </button>
            </div>
            <BaseSelect v-model="newInst.supplier">
              <option value="">{{ $t('common.none') }}</option>
              <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
            </BaseSelect>
            <div v-if="showNewSupplierForm" class="mt-3 p-4 bg-[var(--color-surface)] border border-blue-900 rounded-xl">
              <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('itemView.newSupplierTitle') }}</div>
              <BaseFormField :label="$t('common.name')" required>
                <BaseInput v-model="newSupplierForm.name" placeholder="Leverandørnavn AS" />
              </BaseFormField>
              <BaseFormField :label="$t('itemView.contactPerson')">
                <BaseInput v-model="newSupplierForm.contact_person" placeholder="Ola Nordmann" />
              </BaseFormField>
              <div class="flex gap-2 justify-end">
                <button type="button" @click="toggleNewSupplierForm" class="btn ghost small">{{ $t('common.cancel') }}</button>
                <button type="button" @click="saveNewSupplier" :disabled="savingSupplier || !newSupplierForm.name" class="btn primary small">
                  {{ savingSupplier ? $t('common.saving') : $t('common.create') }}
                </button>
              </div>
            </div>
          </BaseFormField>

          <BaseFormField>
            <div class="flex items-center justify-start mb-2">
              <label class="form-label mb-0">{{ $t('itemView.depreciationProfile') }}</label>
              <button type="button" @click="toggleNewDepreciationProfileForm" class="btn primary small ml-6">
                {{ showNewDepreciationProfileForm ? $t('itemView.cancelNewArea') : $t('itemView.newProfile') }}
              </button>
            </div>
            <BaseSelect v-model="newInst.depreciation_profile">
              <option value="">{{ $t('itemView.depreciationProfileNone') }}</option>
              <option v-for="p in depreciationProfiles" :key="p.id" :value="p.id">{{ p.name }}</option>
            </BaseSelect>
            <div v-if="showNewDepreciationProfileForm" class="mt-3 p-4 bg-[var(--color-surface)] border border-blue-900 rounded-xl">
              <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('itemView.newProfileTitle') }}</div>
              <BaseFormField :label="$t('common.name')" required>
                <BaseInput v-model="newDepreciationProfileForm.name" placeholder="Standard 20% avskrivning" />
              </BaseFormField>
              <div class="form-row">
                <BaseFormField :label="$t('depreciation.methodLabel')" required>
                  <BaseSelect v-model="newDepreciationProfileForm.method">
                    <option value="STRAIGHT_LINE">{{ $t('depreciation.methods.STRAIGHT_LINE') }}</option>
                    <option value="DECLINING_BALANCE">{{ $t('depreciation.methods.DECLINING_BALANCE') }}</option>
                    <option value="INSURANCE_STEP">{{ $t('depreciation.methods.INSURANCE_STEP') }}</option>
                  </BaseSelect>
                </BaseFormField>
                <BaseFormField :label="$t('itemView.annualRate')" required :hint="$t('itemView.annualRateHint')">
                  <BaseInput type="number" min="0" max="1" step="0.01" v-model="newDepreciationProfileForm.annual_rate" placeholder="0.20" />
                </BaseFormField>
              </div>
              <BaseFormField v-if="newDepreciationProfileForm.method === 'INSURANCE_STEP'" :label="$t('depreciation.gracePeriod')" :hint="$t('itemView.gracePeriodHint')">
                <BaseInput type="number" min="0" v-model="newDepreciationProfileForm.grace_period_years" placeholder="0" />
              </BaseFormField>
              <div class="flex gap-2 justify-end">
                <button type="button" @click="toggleNewDepreciationProfileForm" class="btn ghost small">{{ $t('common.cancel') }}</button>
                <button type="button" @click="saveNewDepreciationProfile" :disabled="savingDepreciationProfile || !newDepreciationProfileForm.name || !newDepreciationProfileForm.annual_rate" class="btn primary small">
                  {{ savingDepreciationProfile ? $t('common.saving') : $t('common.create') }}
                </button>
              </div>
            </div>
          </BaseFormField>

          <div v-if="addInstError" class="text-xs text-red-400 mb-2">{{ addInstError }}</div>
          <div v-if="!isNew" class="flex gap-2 justify-end mt-2">
            <button class="btn ghost small" @click="cancelAddForm">{{ $t('common.cancel') }}</button>
            <button class="btn primary small" :disabled="addingInst || !newInstValid" @click="submitAddInstance">
              {{ addingInst ? $t('common.adding') : $t('itemView.addInstance') }}
            </button>
          </div>
        </div>

        <!-- Instances list -->
        <EmptyState v-if="!isNew && instancesLoading" :message="$t('itemView.loadingInstances')" />
        <EmptyState v-else-if="!isNew && !instances.length && !showAddForm" :message="$t('itemView.noInstances')" />
        <div v-else-if="!isNew && instances.length" class="flex flex-col gap-1.5 mt-1 pb-2">
          <div
            v-for="inst in instances"
            :key="inst.id"
            class="flex items-center gap-3 px-3.5 py-3 bg-[var(--color-surface)] border border-gray-800 rounded-xl cursor-pointer transition-colors hover:bg-gray-800"
            @click="goToInstance(inst)"
          >
            <div class="flex-1 min-w-0">
              <div class="text-sm font-semibold truncate">{{ inst.assetTag || inst.asset_tag || inst.serial_number || '#' + inst.id }}</div>
              <div class="flex gap-2 flex-wrap mt-0.5">
                <span v-if="(inst.assetTag || inst.asset_tag) && inst.serial_number" class="text-xs text-gray-500">SN: {{ inst.serial_number }}</span>
                <span v-if="inst.area" class="text-xs text-[var(--color-muted)] flex items-center gap-1">
                  <MdiMapMarkerOutline class="w-3 h-3 shrink-0" />
                  <span>{{ [inst.area.warehouse?.location?.name, inst.area.warehouse?.name, inst.area.name].filter(Boolean).join(' › ') }}</span>
                </span>
                <span v-if="form.item_type === 3 && inst.available_quantity != null" class="text-xs text-gray-500">{{ $t('itemView.qtyLabel') }} {{ inst.available_quantity }}</span>
                <span v-if="inst.in_downtime" class="flex items-center gap-1 text-xs text-amber-300"><MdiWrench class="w-3 h-3" /> {{ $t('nav.maintenance') }}</span>
              </div>
            </div>
            <div class="flex items-center gap-2 shrink-0">
              <span :class="instStatusClass(inst)">{{ instStatusLabel(inst) }}</span>
              <button
                class="flex items-center justify-center w-7 h-7 rounded border-none cursor-pointer transition-colors disabled:opacity-40 shrink-0"
                :class="inst.in_downtime ? 'bg-green-900 text-green-300 hover:bg-green-800' : 'bg-amber-900 text-amber-200 hover:bg-amber-800'"
                :disabled="togglingDowntime"
                :title="inst.in_downtime ? $t('itemView.returnToServiceBtn') : $t('itemView.markMaintenanceBtn')"
                @click.stop="inst.in_downtime ? openReturnService(inst.id) : openMarkMaintenance(inst.id)"
              >
                <svg v-if="!inst.in_downtime" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></svg>
                <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5"><polyline points="20 6 9 17 4 12"/></svg>
              </button>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="w-3.5 h-3.5 text-gray-600">
                <path d="m9 18 6-6-6-6"/>
              </svg>
            </div>
          </div>
        </div>        
      </div>

      <BaseEditorSection v-if="showFullEditor" :title="$t('itemView.pricingPurchasing')" collapsible>
          <BaseFormField :label="$t('itemView.rentalPrice')">
            <div class="relative">
              <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--color-muted)] text-sm">kr </span>
              <BaseInput style="padding-left: 2rem;" type="number" min="0" step="0.01" v-model="form.rental_price_per_day" placeholder="0.00" />
            </div>
          </BaseFormField>

          <BaseFormField :label="$t('itemView.defaultDepreciationProfile')" :hint="$t('itemView.defaultDepreciationHint')">
            <BaseSelect v-model="form.depreciation_profile">
              <option value="">{{ $t('itemView.defaultDepreciationProfileNone') }}</option>
              <option v-for="p in depreciationProfiles" :key="p.id" :value="p.id">{{ p.name }}</option>
            </BaseSelect>
          </BaseFormField>
        <template v-if="form.item_type === 1 || form.item_type === 3 || form.item_type === 4">
        <BaseFormField :label="$t('itemView.purchasePrice')">
          <div class="relative">
            <span class="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm">kr</span>
            <BaseInput style="padding-left: 1.75rem;" type="number" min="0" step="0.01" v-model="uniqueInst.purchase_price" placeholder="0.00" />
          </div>
        </BaseFormField>

        <BaseFormField>
          <div class="flex items-center justify-start mb-2">
            <label class="form-label mb-0">{{ $t('assetValues.groupOptions.supplier') }}</label>
            <button type="button" @click="toggleNewSupplierForm" class="btn primary small ml-6">
              {{ showNewSupplierForm ? $t('itemView.cancelNewArea') : $t('itemView.newSupplier') }}
            </button>
          </div>
          <BaseSelect v-model="uniqueInst.supplier">
            <option value="">{{ $t('common.none') }}</option>
            <option v-for="s in suppliers" :key="s.id" :value="s.id">{{ s.name }}</option>
          </BaseSelect>
          <div v-if="showNewSupplierForm" class="mt-3 p-4 bg-[var(--color-surface)] border border-blue-900 rounded-xl">
            <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('itemView.newSupplierTitle') }}</div>
            <BaseFormField :label="$t('common.name')" required>
              <BaseInput v-model="newSupplierForm.name" placeholder="Leverandørnavn AS" />
            </BaseFormField>
            <BaseFormField :label="$t('itemView.contactPerson')">
              <BaseInput v-model="newSupplierForm.contact_person" placeholder="Ola Nordmann" />
            </BaseFormField>
            <div class="flex gap-2 justify-end">
              <button type="button" @click="toggleNewSupplierForm" class="btn ghost small">{{ $t('common.cancel') }}</button>
              <button type="button" @click="saveNewSupplier" :disabled="savingSupplier || !newSupplierForm.name" class="btn primary small">
                {{ savingSupplier ? $t('common.saving') : $t('common.create') }}
              </button>
            </div>
          </div>
        </BaseFormField>

        <BaseFormField>
          <div class="flex items-center justify-start mb-2">
            <label class="form-label mb-0">{{ $t('itemView.depreciationProfile') }}</label>
            <button type="button" @click="toggleNewDepreciationProfileForm" class="btn primary small ml-6">
              {{ showNewDepreciationProfileForm ? $t('itemView.cancelNewArea') : $t('itemView.newProfile') }}
            </button>
          </div>
          <BaseSelect v-model="uniqueInst.depreciation_profile">
            <option value="">{{ $t('itemView.depreciationProfileNone') }}</option>
            <option v-for="p in depreciationProfiles" :key="p.id" :value="p.id">{{ p.name }}</option>
          </BaseSelect>
          <div v-if="showNewDepreciationProfileForm" class="mt-3 p-4 bg-[var(--color-surface)] border border-blue-900 rounded-xl">
            <div class="text-sm font-bold text-blue-400 mb-3">{{ $t('itemView.newProfileTitle') }}</div>
            <BaseFormField :label="$t('common.name')" required>
              <BaseInput v-model="newDepreciationProfileForm.name" placeholder="Standard 20% avskrivning" />
            </BaseFormField>
            <div class="form-row">
              <BaseFormField :label="$t('depreciation.methodLabel')" required>
                <BaseSelect v-model="newDepreciationProfileForm.method">
                  <option value="STRAIGHT_LINE">{{ $t('depreciation.methods.STRAIGHT_LINE') }}</option>
                  <option value="DECLINING_BALANCE">{{ $t('depreciation.methods.DECLINING_BALANCE') }}</option>
                  <option value="INSURANCE_STEP">{{ $t('depreciation.methods.INSURANCE_STEP') }}</option>
                </BaseSelect>
              </BaseFormField>
              <BaseFormField :label="$t('itemView.annualRate')" required :hint="$t('itemView.annualRateHint')">
                <BaseInput type="number" min="0" max="1" step="0.01" v-model="newDepreciationProfileForm.annual_rate" placeholder="0.20" />
              </BaseFormField>
            </div>
            <BaseFormField v-if="newDepreciationProfileForm.method === 'INSURANCE_STEP'" :label="$t('depreciation.gracePeriod')" :hint="$t('itemView.gracePeriodHint')">
              <BaseInput type="number" min="0" v-model="newDepreciationProfileForm.grace_period_years" placeholder="0" />
            </BaseFormField>
            <div class="flex gap-2 justify-end">
              <button type="button" @click="toggleNewDepreciationProfileForm" class="btn ghost small">{{ $t('common.cancel') }}</button>
              <button type="button" @click="saveNewDepreciationProfile" :disabled="savingDepreciationProfile || !newDepreciationProfileForm.name || !newDepreciationProfileForm.annual_rate" class="btn primary small">
                {{ savingDepreciationProfile ? $t('common.saving') : $t('common.create') }}
              </button>
            </div>
          </div>
        </BaseFormField>

        </template>
      </BaseEditorSection>
      <BaseEditorSection v-if="showFullEditor" :title="$t('itemView.additionalSettings')" collapsible>
        <BaseFormField v-if="showFullEditor">
          <BaseCheckbox
            v-model="form.available_in_portal"
            :label="$t('bulkEdit.fields.available_in_portal')"
            :disabled="!isOperatorOrAbove || saving"
          />
        </BaseFormField>

        <ItemContractAddition
          v-model:kind="form.contract_addition_type"
          v-model:html="form.contract_addition_html"
          v-model:file="additionFile"
          :filename="additionFilename"
          :item-id="isNew ? null : itemId"
          :disabled="!isOperatorOrAbove || saving"
        />


          <BaseFormField :label="$t('itemView.wikiPage')">
            <BaseSelect v-model="form.wiki_page">
              <option value="">{{ $t('common.none') }}</option>
              <option v-for="wp in wikiPages" :key="wp.id" :value="wp.id">{{ wp.name }}</option>
            </BaseSelect>
            <div v-if="form.wiki_page" class="form-hint mt-1.5">
              <router-link :to="`/wiki/${form.wiki_page}`" class="text-blue-400 no-underline">{{ $t('itemView.viewWikiPage') }}</router-link>
            </div>
          </BaseFormField>

      </BaseEditorSection>

      <!-- ── Value & Depreciation (types 1, 3, 4) — office / full editor only ─ -->
      <template v-if="showFullEditor">
      <!-- ── Value & Depreciation (types 1, 3, 4) ─────────────────── -->
      <div v-if="!isNew && (form.item_type === 1 || form.item_type === 3 || form.item_type === 4) && uniqueInstId" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <MdiTrendingDown class="w-4 h-4 text-amber-400 shrink-0" />
          <div class="section-label" style="margin-bottom:0">{{ $t('itemView.valueDepreciation') }}</div>
          <span v-if="depBookValue !== null" class="ml-auto badge badge-accent badge-pill text-xs">
            {{ fmtCurrency(depBookValue) }}
          </span>
        </div>

        <div v-if="!depPurchasePrice || !depPurchaseDate" class="flex items-start gap-2 text-xs text-[var(--color-muted)] bg-[var(--color-surface)] rounded-xl px-3 py-2.5 border border-gray-800">
          <MdiInformationOutline class="w-4 h-4 shrink-0 mt-0.5 text-gray-600" />
          {{ $t('itemView.noPurchaseInfo') }}
        </div>
        <div v-else-if="!depProfile" class="flex items-start gap-2 text-xs text-[var(--color-muted)] bg-[var(--color-surface)] rounded-xl px-3 py-2.5 border border-gray-800">
          <MdiInformationOutline class="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
          {{ $t('itemView.noDepProfile') }}
        </div>
        <div v-else class="flex flex-col gap-3">
          <div class="grid grid-cols-3 gap-2">
            <div class="bg-[var(--color-surface)] border border-gray-800 rounded-xl px-3 py-2.5 flex flex-col gap-0.5">
              <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('itemView.statPurchase') }}</span>
              <span class="text-sm font-semibold text-gray-200">{{ fmtCurrency(depPurchasePrice) }}</span>
            </div>
            <div class="bg-[var(--color-surface)] border border-gray-800 rounded-xl px-3 py-2.5 flex flex-col gap-0.5">
              <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('assetValues.bookValue') }}</span>
              <span class="text-sm font-semibold text-amber-300">{{ fmtCurrency(depBookValue) }}</span>
            </div>
            <div class="bg-[var(--color-surface)] border border-gray-800 rounded-xl px-3 py-2.5 flex flex-col gap-0.5">
              <span class="text-[0.65rem] text-[var(--color-muted)] uppercase tracking-wide">{{ $t('assetValues.depreciatedCol') }}</span>
              <span class="text-sm font-semibold" :class="depPct > 75 ? 'text-red-400' : depPct > 40 ? 'text-orange-400' : 'text-green-400'">
                {{ depPct !== null ? depPct.toFixed(1) + '%' : '—' }}
              </span>
            </div>
          </div>
          <div class="flex items-center gap-2 text-xs text-[var(--color-muted)] flex-wrap">
            <span class="badge badge-muted badge-pill">{{ depProfileLevel }}</span>
            <span class="font-medium text-gray-300">{{ depProfile.name }}</span>
            <span class="text-gray-600">·</span>
            <span>{{ METHOD_LABELS[depProfile.method] ?? depProfile.method }}</span>
            <span class="text-gray-600">·</span>
            <span>{{ (parseFloat(depProfile.annual_rate) * 100).toFixed(1) }}{{ $t('ui.pA') }}</span>
          </div>
          <div class="relative h-2 bg-gray-800 rounded-full overflow-hidden">
            <div
              class="absolute left-0 top-0 h-full rounded-full transition-all"
              :class="depPct > 75 ? 'bg-red-500' : depPct > 40 ? 'bg-orange-400' : 'bg-amber-400'"
              :style="{ width: (depPct ?? 0) + '%' }"
            />
          </div>
          <button
            class="flex items-center gap-1.5 text-xs text-[var(--color-muted)] hover:text-gray-300 transition-colors cursor-pointer bg-transparent border-none p-0 self-start"
            @click="showDepSchedule = !showDepSchedule"
          >
            <component :is="showDepSchedule ? MdiChevronUp : MdiChevronDown" class="w-4 h-4" />
            {{ showDepSchedule ? $t('itemView.hideSchedule') : $t('itemView.showSchedule') }}
          </button>
          <div v-if="showDepSchedule" class="overflow-x-auto">
            <table class="w-full text-xs text-left border-collapse">
              <thead>
                <tr class="text-[var(--color-muted)] border-b border-gray-800">
                  <th class="py-1.5 pr-4 font-medium">{{ $t('itemView.tableYear') }}</th>
                  <th class="py-1.5 pr-4 font-medium text-right">{{ $t('itemView.tableValueStart') }}</th>
                  <th class="py-1.5 font-medium text-right">{{ $t('itemView.tableDepreciation') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="row in depSchedule"
                  :key="row.year"
                  class="border-b border-gray-900"
                  :class="row.isCurrent ? 'bg-amber-950/30 text-amber-200' : 'text-gray-400'"
                >
                  <td class="py-1.5 pr-4 font-mono">
                    {{ row.year }}
                    <span v-if="row.isCurrent" class="ml-1.5 badge badge-warning badge-pill text-[0.6rem]">{{ $t('itemView.nowBadge') }}</span>
                  </td>
                  <td class="py-1.5 pr-4 text-right font-mono">{{ fmtCurrency(row.value) }}</td>
                  <td class="py-1.5 text-right font-mono text-red-400/80">-{{ fmtCurrency(row.depreciation) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
      </template>

      <!-- ── Contained Items (type 1 container) ─────────────────── -->
      <ContainedItemsSection
        v-if="!isNew && form.is_container && form.item_type === 1 && uniqueInstId"
        :parent-id="uniqueInstId"
      />

      <!-- ── Type 1: Availability Timeline ────────────────────────── -->
      <div v-if="!isNew && form.item_type === 1 && (fleetTimelineSegments.length || fleetLoading)" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <MdiChartTimeline class="w-4 h-4 text-[var(--color-muted)]" />
          <div class="section-label" style="margin-bottom:0">{{ $t('itemView.availabilityTimeline') }}</div>
          <div class="flex items-center gap-3 ml-auto text-xs text-[var(--color-muted)]">
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-blue-500/40 border border-blue-500/50"></span> {{ $t('itemView.legendActive') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-amber-600/30 border border-amber-600/40"></span> {{ $t('itemView.legendOpenEnded') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-gray-600/40 border border-gray-600/40"></span> {{ $t('itemView.legendReturned') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-purple-500/30 border border-purple-500/40"></span> {{ $t('itemView.legendPlanned') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm border border-dashed border-purple-500/60"></span> {{ $t('itemView.legendTentative') }}</span>
          </div>
        </div>
        <div v-if="fleetLoading" class="state-empty text-sm py-3">{{ $t('ui.loadingTimeline') }}</div>
        <ItemAvailabilityTimeline v-else :checkouts="fleetTimelineSegments" :future-days="120" />
      </div>

      <!-- ── Type 3: Stock & Availability ─────────────────────────── -->
      <div v-if="!isNew && form.item_type === 3 && (fleetTimelineSegments.length || fleetLoading || uniqueInst.available_quantity !== '')" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <MdiChartTimeline class="w-4 h-4 text-[var(--color-muted)]" />
          <div class="section-label" style="margin-bottom:0">{{ $t('itemView.stockAvailability') }}</div>
        </div>
        <div v-if="fleetLoading" class="state-empty text-sm py-3">{{ $t('common.loading') }}</div>
        <ItemStockTimeline
          v-else
          :checkouts="fleetTimelineSegments"
          :future-days="120"
          :current-qty="uniqueInst.available_quantity !== '' ? Number(uniqueInst.available_quantity) : null"
          :total-qty="uniqueInst.available_quantity !== '' ? Number(uniqueInst.available_quantity) : null"
        />
      </div>

      <!-- ── Type 4: Stock & Availability ─────────────────────────── -->
      <div v-if="!isNew && form.item_type === 4 && (fleetTimelineSegments.length || fleetLoading || uniqueInst.available_quantity !== '')" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <MdiChartTimeline class="w-4 h-4 text-[var(--color-muted)]" />
          <div class="section-label" style="margin-bottom:0">{{ $t('itemView.stockAvailability') }}</div>
        </div>
        <div v-if="fleetLoading" class="state-empty text-sm py-3">{{ $t('common.loading') }}</div>
        <ItemStockTimeline
          v-else
          :checkouts="fleetTimelineSegments"
          :future-days="120"
          :current-qty="uniqueInst.availability_net_quantity != null ? Number(uniqueInst.availability_net_quantity) : (uniqueInst.available_quantity !== '' ? Number(uniqueInst.available_quantity) : null)"
          :total-qty="uniqueInst.available_quantity !== '' ? Number(uniqueInst.available_quantity) : null"
          :restock-threshold="uniqueInst.restock_threshold !== '' ? Number(uniqueInst.restock_threshold) : null"
          :is-consumable="true"
        />
      </div>

      <!-- ── Checkout History (types 1, 3, 4) ─────────────────────── -->
      <div v-if="!isNew && (form.item_type === 1 || form.item_type === 3 || form.item_type === 4) && fleetCheckouts.length" class="px-4 pt-5 pb-2 border-b border-gray-800">
        <div class="section-label">{{ $t('checkoutsOverview.historyTitle') }}</div>
        <ul class="flex flex-col gap-2">
          <li
            v-for="co in fleetCheckouts"
            :key="co.id"
            class="flex items-start gap-3 px-3.5 py-2.5 bg-[var(--color-surface)] border border-gray-800 rounded-xl"
          >
            <div class="w-2 h-2 rounded-full mt-1.5 shrink-0" :class="co.actual_checkin ? 'bg-gray-600' : 'bg-blue-500'" />
            <div class="flex-1 min-w-0">
              <div class="flex items-center gap-2 flex-wrap">
                <span class="text-sm font-medium text-gray-200">{{ co.user || co.checked_out_by || $t('common.unknown') }}</span>
                <span v-if="co.project" class="text-xs text-gray-500">· {{ co.project }}</span>
              </div>
              <div class="text-xs text-gray-500 mt-0.5">
                <span>{{ fmtDatetime(co.checkout_date) }}</span>
                <span v-if="co.actual_checkin"> {{ $t('ui.returned') }} {{ fmtDatetime(co.actual_checkin) }}</span>
                <span v-else-if="co.effective_expected_checkin" class="text-blue-400"> {{ $t('ui.expected') }} {{ fmtDatetime(co.effective_expected_checkin) }}</span>
                <span v-else class="text-amber-400"> {{ $t('itemView.openEnded') }}</span>
              </div>
            </div>
            <span v-if="!co.actual_checkin" class="badge badge-pill shrink-0 text-[0.65rem]" :class="co.effective_expected_checkin ? 'badge-accent' : 'badge-warning'">{{ co.effective_expected_checkin ? $t('checkoutsOverview.out') : $t('itemView.checkoutStatusOpen') }}</span>
            <span v-else class="badge badge-muted badge-pill shrink-0 text-[0.65rem]">{{ $t('checkoutsOverview.returned') }}</span>
          </li>
        </ul>
        <div v-if="historyTruncated" class="text-xs text-[var(--color-muted)] text-center pt-2 pb-1">
          {{ $t('itemView.showingRecent', { count: fleetCheckouts.length }) }}
        </div>
      </div>

      <!-- ── Upcoming Project Plans (types 1, 3, 4) ────────────────── -->
      <div v-if="!isNew && (form.item_type === 1 || form.item_type === 3 || form.item_type === 4)" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <div class="section-label" style="margin-bottom:0">{{ $t('itemView.projectPlans') }}</div>
          <div class="flex items-center gap-1 ml-2">
            <button v-for="f in planFilters" :key="f.v"
              class="btn ghost small text-xs px-2 py-0.5"
              :class="plansFilter === f.v ? 'opacity-100 font-semibold' : 'opacity-50'"
              @click="plansFilter = f.v; loadPlans(1)"
            >{{ f.l }}</button>
          </div>
          <span v-if="plansTotalCount > 0" class="text-xs text-[var(--color-muted)] ml-auto">{{ $t('ui.totalCount', { count: plansTotalCount }) }}</span>
        </div>
        <div v-if="plansLoading" class="state-empty text-sm py-3">{{ $t('common.loading') }}</div>
        <div v-else-if="!projectPlans.length" class="state-empty text-sm py-2">{{ $t('itemView.noPlans', { filter: currentPlanFilterLabel }) }}</div>
        <ul v-else class="flex flex-col gap-2">
          <li
            v-for="plan in projectPlans"
            :key="plan.id"
            class="flex items-center gap-3 px-3.5 py-2.5 bg-[var(--color-surface)] border border-gray-800 rounded-xl"
          >
            <MdiFolderOutline class="w-4 h-4 text-blue-400 shrink-0" />
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium text-gray-200 truncate">
                {{ plan.project ? (projectNameMap[plan.project] ?? $t('ui.projectFallback', { id: plan.project })) : $t('common.unknown') }}
              </div>
              <div class="text-xs text-gray-500">
                {{ plan.item_name ?? '' }}<span v-if="plan.item_name"> · </span>{{ $t('itemView.qtyLabel') }} {{ plan.quantity ?? 1 }}
                <span v-if="plan.checkout_status && plan.checkout_status !== 'none'" class="ml-2 badge badge-muted text-[0.65rem]">{{ plan.checkout_status }}</span>
              </div>
            </div>
            <router-link v-if="plan.project" :to="`/project/${plan.project}/items`" class="text-xs text-blue-400 hover:underline no-underline whitespace-nowrap">{{ $t('ui.view') }}</router-link>
          </li>
        </ul>
        <div v-if="plansTotalPages > 1" class="flex items-center justify-center gap-3 mt-3 text-sm">
          <button class="btn ghost small" :disabled="plansPage === 1 || plansLoading" @click="loadPlans(plansPage - 1)">{{ $t('documents.prevPage') }}</button>
          <span class="text-[var(--color-muted)] text-xs">{{ plansPage }} / {{ plansTotalPages }}</span>
          <button class="btn ghost small" :disabled="plansPage >= plansTotalPages || plansLoading" @click="loadPlans(plansPage + 1)">{{ $t('checkout.nextArrow') }}</button>
        </div>
      </div>

      <!-- ── Type 2: Fleet Availability ───────────────────────────── -->
      <div v-if="!isNew && form.item_type === 2 && instances.length" class="px-4 pt-5 pb-4 border-b border-gray-800">
        <div class="flex items-center gap-2 mb-3">
          <MdiChartTimeline class="w-4 h-4 text-[var(--color-muted)]" />
          <div class="section-label" style="margin-bottom:0">{{ $t('itemView.fleetAvailability') }}</div>
          <!-- Available count summary -->
          <div class="ml-auto flex items-center gap-1.5">
            <span class="text-lg font-bold text-emerald-400">{{ instances.filter(i => i.status === 1 && !i.is_checked_out && !i.is_planned).length }}</span>
            <span class="text-sm text-[var(--color-muted)]">/ {{ $t('ui.availableCount', { count: instances.length }) }}</span>
          </div>
        </div>
        <div v-if="fleetLoading" class="state-empty text-sm py-3">{{ $t('itemView.fleetLoading') }}</div>
        <template v-else>
          <div class="flex items-center gap-3 mb-3 text-xs text-[var(--color-muted)]">
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-blue-500/40 border border-blue-500/50"></span> {{ $t('itemView.legendActive') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-amber-600/30 border border-amber-600/40"></span> {{ $t('itemView.legendOpenEnded') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-gray-600/40 border border-gray-600/40"></span> {{ $t('itemView.legendReturned') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm bg-purple-500/30 border border-purple-500/40"></span> {{ $t('itemView.legendPlanned') }}</span>
            <span class="flex items-center gap-1"><span class="inline-block w-3 h-2.5 rounded-sm border border-dashed border-purple-500/60"></span> {{ $t('itemView.legendTentative') }}</span>
          </div>
          <ItemAvailabilityTimeline :checkouts="fleetTimelineSegments" :future-days="120" />
        </template>
      </div>

      <div v-if="!loading && !loadError" class="px-4 py-5 border-t border-gray-800 bg-[var(--color-bg)] sticky bottom-0 z-10 flex justify-end">
        <template v-if="isNew">
          <button class="btn ghost" :disabled="saving || !formValid" @click="save(true)">
            {{ saving ? $t('common.saving') : $t('itemView.createAddNew') }}
          </button>
          <button class="btn primary" :disabled="saving || !formValid" @click="save()">
            {{ saving ? $t('common.saving') : $t('common.create') }}
          </button>
        </template>
        <template v-else>
          <button class="btn ghost" :disabled="saving || !formValid" @click="save(false, true)">
            {{ saving ? $t('common.saving') : $t('itemView.saveClose') }}
          </button>
          <button class="btn primary" :disabled="saving || !formValid" @click="save()">
            {{ saving ? $t('common.saving') : $t('common.save') }}
          </button>
        </template>
      </div>
      <div v-if="!isNew" class="px-4 py-5">
        <div class="section-label danger">{{ $t('common.dangerZone') }}</div>
        <button class="btn danger w-full" :disabled="deleting" @click="onDelete">
          {{ deleting ? $t('common.deleting') : $t('itemView.deleteItemType') }}
        </button>
      </div>



    </div>

    <!-- Add to maintenance schedule modal -->
    <div v-if="showAddScheduleModal" class="modal-overlay" @click.self="closeAddScheduleModal">
      <div class="confirm-modal max-h-[85vh] overflow-y-auto">
        <div class="confirm-modal__title">{{ $t('itemView.addToSchedule') }}</div>
        <BaseFormField>
          <BaseInput
            v-model="addScheduleSearch"
            autocomplete="off"
            :placeholder="$t('itemView.searchSchedules')"
            @update:model-value="onAddScheduleSearch"
          />
        </BaseFormField>
        <div v-if="addScheduleLoading" class="text-xs text-gray-500 text-center py-3">{{ $t('itemView.searching') }}</div>
        <div v-else-if="addScheduleResults.length === 0 && addScheduleSearch.trim()" class="text-xs text-gray-500 text-center py-3">{{ $t('maintenance.noSchedules') }}</div>
        <div v-else-if="addScheduleResults.length === 0" class="text-xs text-gray-500 text-center py-3">{{ $t('itemView.typeToSearch') }}</div>
        <div class="flex flex-col gap-1 mb-1">
          <button
            v-for="s in addScheduleResults"
            :key="s.id"
            class="flex items-center gap-2 w-full bg-[var(--color-surface)] border border-gray-700 rounded-lg px-3 py-2.5 cursor-pointer text-left transition-colors hover:bg-gray-800 hover:border-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="addingScheduleId === s.id"
            @click="addToSchedule(s)"
          >
            <div class="flex-1 text-sm font-semibold text-gray-100">{{ s.title }}</div>
            <div class="flex gap-1.5 items-center">
              <span v-if="s.next_due_date" class="text-xs text-gray-400">{{ $t('ui.due') }} {{ fmtDatetime(s.next_due_date) }}</span>
              <span v-if="s.frequency" class="text-[0.7rem] text-gray-500 bg-slate-900 px-1 rounded">{{ s.frequency }}</span>
            </div>
            <span v-if="addingScheduleId === s.id" class="text-xs text-blue-400 whitespace-nowrap">{{ $t('common.adding') }}</span>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="w-4 h-4 text-gray-600 shrink-0">
              <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
            </svg>
          </button>
        </div>
        <div class="confirm-modal__actions" style="margin-top:.75rem">
          <button class="confirm-btn cancel" @click="closeAddScheduleModal">{{ $t('common.close') }}</button>
        </div>
      </div>
    </div>

    <!-- Mark maintenance needed modal -->
    <div v-if="showMarkDowntimeModal" class="modal-overlay" @click.self="showMarkDowntimeModal = false">
      <div class="confirm-modal">
        <div class="confirm-modal__title">{{ $t('itemView.markMaintenanceTitle') }}</div>
        <div class="confirm-modal__body">{{ $t('itemView.markMaintenanceBody') }}</div>
        <BaseFormField :label="$t('itemView.downtimeReason')" style="margin-bottom:1rem">
          <BaseInput v-model="markDowntimeReason" :placeholder="$t('itemView.describeIssuePlaceholder')" />
        </BaseFormField>
        <div class="confirm-modal__actions">
          <button class="confirm-btn cancel" @click="showMarkDowntimeModal = false">{{ $t('common.cancel') }}</button>
          <button class="confirm-btn" style="background:#d97706;color:#fff" :disabled="togglingDowntime" @click="confirmMarkMaintenance">
            {{ togglingDowntime ? $t('common.saving') : $t('itemView.markMaintenanceConfirmBtn') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showReturnServiceModal" class="modal-overlay" @click.self="showReturnServiceModal = false">
      <div class="confirm-modal">
        <div class="confirm-modal__title">{{ $t('itemView.returnToServiceTitle') }}</div>
        <div class="confirm-modal__body">{{ $t('itemView.returnToServiceBody') }}</div>
        <div class="confirm-modal__actions">
          <button class="confirm-btn cancel" @click="showReturnServiceModal = false">{{ $t('common.cancel') }}</button>
          <button class="confirm-btn" style="background:#16a34a;color:#fff" :disabled="togglingDowntime" @click="confirmReturnService">
            {{ togglingDowntime ? $t('common.saving') : $t('itemView.returnToServiceBtn') }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showDeleteConfirm" class="modal-overlay" @click.self="showDeleteConfirm = false">
      <div class="confirm-modal">
        <div class="confirm-modal__title">{{ $t('itemView.deleteItemTypeTitle') }}</div>
        <div class="confirm-modal__body">
          {{ $t('ui.deleteItemTypeConfirmation', { name: form.name }) }}
        </div>
        <div class="confirm-modal__actions">
          <button class="confirm-btn cancel" @click="showDeleteConfirm = false">{{ $t('common.cancel') }}</button>
          <button class="confirm-btn delete" :disabled="deleting" @click="confirmDelete">
            {{ deleting ? $t('common.deleting') : $t('common.delete') }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import BaseEditorSection from '@/components/base/BaseEditorSection.vue'
import ItemContractAddition from '@/components/ItemContractAddition.vue'
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useStore } from '@/stores/store'
import { useAuthStore } from '@/stores/auth'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import { useShellModeStore } from '@/stores/shellMode.js'
import {
  getItemById, createItem, putItem, deleteItem,
  getItemInstances, createItemInstance, updateItemInstance,
  getAreas, getLocations, getWarehouses, getWikiPages, createCategory, createArea,
  patchItemMetadataDowntime, getCheckoutHistoryForInstance,
  getSuppliers, createSupplier,
  getDepreciationProfiles, createDepreciationProfile,
  getActiveCheckouts, getProjectPlansForInstance, getProject,
  getMaintenanceSchedules, updateMaintenanceSchedule,
  getItemInstance, getItemMetadataTimeline, getItemFleetTimeline,
} from '@/api/api.js'
import MdiWrench from '~icons/mdi/wrench'
import MdiChartTimeline from '~icons/mdi/chart-timeline-variant'
import MdiAccountOutline from '~icons/mdi/account-outline'
import MdiFolderOutline from '~icons/mdi/folder-outline'
import MdiCalendarOutline from '~icons/mdi/calendar-outline'
import MdiCalendarArrowRight from '~icons/mdi/calendar-arrow-right'
import MdiCheckCircleOutline from '~icons/mdi/check-circle-outline'
import MdiAlertCircleOutline from '~icons/mdi/alert-circle-outline'
import MdiArchiveOutline from '~icons/mdi/archive-outline'
import MdiSwapHorizontal from '~icons/mdi/swap-horizontal'
import MdiTrendingDown from '~icons/mdi/trending-down'
import MdiChevronDown from '~icons/mdi/chevron-down'
import MdiChevronUp from '~icons/mdi/chevron-up'
import MdiInformationOutline from '~icons/mdi/information-outline'
import MdiPackageVariantClosed from '~icons/mdi/package-variant-closed'
import MdiMapMarkerOutline from '~icons/mdi/map-marker-outline'
import BasePageHeader from '@/components/base/BasePageHeader.vue'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseTextarea from '@/components/base/BaseTextarea.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import EmptyState from '@/components/base/EmptyState.vue'
import ContainedItemsSection from '@/components/ContainedItemsSection.vue'
import ItemAvailabilityTimeline from '@/components/ItemAvailabilityTimeline.vue'
import ItemStockTimeline from '@/components/ItemStockTimeline.vue'

const router = useRouter()
const route  = useRoute()
const store  = useStore()
const auth   = useAuthStore()
const toast  = useToast()
const { t }  = useI18n()
const { isOperatorOrAbove } = usePermissions()
const shell = useShellModeStore()

const itemId = computed(() => route.params.itemId)
const isNew  = computed(() => !itemId.value || itemId.value === 'new')
/** Warehouse glance hides catalog-admin density (price, depreciation, wiki). */
const isWarehouseGlance = computed(() => shell.isWarehouse && !isNew.value && route.query.edit !== 'full')
const showFullEditor = computed(() => !isWarehouseGlance.value)

// ── form ───────────────────────────────────────────────────────────────
const form = ref({ name: '', category: '', item_type: '', description: '', rental_price_per_day: '', wiki_page: '', depreciation_profile: '', is_container: false, available_in_portal: false, contract_addition_type: 'none', contract_addition_html: '' })
const additionFile = ref(null)
const additionFilename = ref('')

// Track the item_type as saved in DB — used to lock the selector for types 2/3/4
const savedItemType = ref(null)
const typeChangeLocked = computed(() => !isNew.value && savedItemType.value != null && savedItemType.value !== 1)

const formValid = computed(() => {
  if (!form.value.name.trim() || form.value.category === '' || form.value.item_type === '') return false
  if (isNew.value) {
    if (form.value.item_type === 1 || form.value.item_type === 3) return uniqueInst.value.area !== ''
    if (form.value.item_type === 2) return newInst.value.area !== ''
    if (form.value.item_type === 4) return uniqueInst.value.available_quantity !== '' && uniqueInst.value.area !== ''
  }
  return true
})

const itemTypeHint = computed(() => ({
  1: t('itemView.typeHint.unique'),
  2: t('itemView.typeHint.multi'),
  3: t('itemView.typeHint.quantity'),
  4: t('itemView.typeHint.consumable'),
}[form.value.item_type] ?? ''))

// ── load ──────────────────────────────────────────────────────────────
const loading   = ref(false)
const loadError = ref(null)
const saving    = ref(false)
const deleting  = ref(false)
const showDeleteConfirm      = ref(false)
const togglingDowntime       = ref(false)
const showMarkDowntimeModal  = ref(false)
const markDowntimeReason     = ref('')
const showReturnServiceModal = ref(false)
const downtimeTargetId       = ref(null)
const categories = ref([])
const areas      = ref([])
const wikiPages  = ref([])
const showNewCategoryForm = ref(false)
const newCategoryForm = ref({ name: '', parent: null })
const savingCategory = ref(false)
const showNewAreaForm = ref(false)
const newAreaForm = ref({ name: '', location: '', warehouse: '' })
const newAreaFormWarehouses = ref([])
const savingArea = ref(false)
let skipAreaLocationWatch = false
const suppliers = ref([])
const showNewSupplierForm = ref(false)
const newSupplierForm = ref({ name: '', contact_person: '' })
const savingSupplier = ref(false)
const depreciationProfiles = ref([])
const showNewDepreciationProfileForm = ref(false)
const newDepreciationProfileForm = ref({ name: '', method: 'STRAIGHT_LINE', annual_rate: '', grace_period_years: '' })
const savingDepreciationProfile = ref(false)

const rootCategories = computed(() => categories.value.filter(c => c.parent === null))
const locations = ref([])

const locationSummary = computed(() => {
  const seen = new Set()
  const result = []
  for (const i of instances.value) {
    const area = i.area
    if (!area) continue
    const areaName      = area.name
    const warehouseName = area.warehouse?.name ?? null
    const locationName  = area.warehouse?.location?.name ?? null
    const key = `${locationName}|${warehouseName}|${areaName}`
    if (!seen.has(key)) {
      seen.add(key)
      result.push({ key, locationName, warehouseName, areaName })
    }
  }
  return result
})

// Show a warning when the item has no instances at the currently selected
// warehouse (or location when no warehouse is selected).
const notAtCurrentFilter = computed(() => {
  if (isNew.value || instancesLoading.value || !instances.value.length) return false
  if (store.currentWarehouse?.id) {
    return !instances.value.some(i => i.area?.warehouse?.id === store.currentWarehouse.id)
  }
  if (store.currentLocation?.id) {
    return !instances.value.some(i => i.area?.warehouse?.location?.id === store.currentLocation.id)
  }
  return false
})

const notAtCurrentFilterLabel = computed(() => {
  if (store.currentWarehouse?.id) return store.currentWarehouse.name ?? t('ui.theSelectedWarehouse')
  if (store.currentLocation?.id)  return store.currentLocation.name  ?? t('ui.theSelectedLocation')
  return ''
})

async function load() {
  if (!isNew.value) {
    loading.value   = true
    loadError.value = null
  }

  try {
    const areaParams = store.currentWarehouse?.id ? { warehouse: store.currentWarehouse.id } : {}

    const fetches = [
      store.categories.length ? Promise.resolve(store.categories) : store.fetchCategories().then(() => store.categories),
      getAreas(areaParams).then(r => r.results ?? r ?? []).catch(() => []),
      getWikiPages().then(r => r.results ?? r ?? []).catch(() => []),
      isNew.value ? Promise.resolve(null) : getItemById(itemId.value),
      getLocations().then(r => r.results ?? r ?? []).catch(() => []),
      getSuppliers().then(r => r.results ?? r ?? []).catch(() => []),
      getDepreciationProfiles().then(r => r.results ?? r ?? []).catch(() => []),
    ]

    const [cats, areaList, wikiList, item, locationList, supplierList, depProfileList] = await Promise.all(fetches)
    locations.value = locationList

    categories.value = cats ?? []
    areas.value      = areaList
    wikiPages.value  = wikiList
    suppliers.value  = supplierList
    depreciationProfiles.value = depProfileList

    if (!isNew.value && item) {
      form.value.name                 = item.name ?? ''
      form.value.category             = typeof item.category === 'object' ? item.category?.id : item.category
      form.value.item_type            = item.item_type ?? ''
      savedItemType.value             = item.item_type ?? null
      form.value.description          = item.description ?? ''
      form.value.contract_addition_type = item.contract_addition_type ?? 'none'
      form.value.contract_addition_html = item.contract_addition_html ?? ''
      additionFilename.value = item.contract_addition_filename ?? ''
      additionFile.value = null
      form.value.rental_price_per_day = item.rental_price_per_day ?? ''
      form.value.wiki_page            = typeof item.wiki_page === 'object' && item.wiki_page ? item.wiki_page.id : (item.wiki_page ?? '')
      form.value.depreciation_profile = (typeof item.depreciation_profile === 'object' ? item.depreciation_profile?.id : item.depreciation_profile) ?? ''
      form.value.available_in_portal   = item.available_in_portal ?? false
      form.value.is_container          = item.is_container ?? false
    }
  } catch {
    if (!isNew.value) loadError.value = t('itemView.loadFailed')
  } finally {
    loading.value = false
  }

  if (!isNew.value) await loadInstances()
}

// ── instances ─────────────────────────────────────────────────────────
const instances        = ref([])
const instancesLoading = ref(false)
const showAddForm      = ref(false)
const addingInst       = ref(false)
const addInstError     = ref(null)
const newInst = ref({ assetTag: '', serial_number: '', area: '', status: 1, comment: '', purchase_price: '', available_quantity: '', supplier: '', depreciation_profile: '' })

const newInstValid = computed(() => newInst.value.area !== '')

// ── unique instance (type 1) ──────────────────────────────────────────
const uniqueInstId  = ref(null)
const uniqueInst    = ref({ assetTag: '', serial_number: '', area: '', status: 1, comment: '', purchase_price: '', available_quantity: '', availability_net_quantity: null, restock_threshold: '', restock_quantity: '', supplier: '', depreciation_profile: '' })
// Full API response retained for the Value & Depreciation section
const uniqueInstRaw = ref(null)
const parentInstance = ref(null)
const showDepSchedule = ref(false)

// ─── Depreciation / Value helpers (types 1, 3, 4) ────────────────────────────

function calcBookValue(purchasePrice, purchaseDate, profile, asOfDate) {
  if (!purchasePrice || !purchaseDate || !profile) return null
  const pp = parseFloat(purchasePrice)
  const salvage = pp * parseFloat(profile.salvage_value_percentage ?? 0)
  const msPurchase = new Date(purchaseDate).getTime()
  const msTarget = asOfDate ? new Date(asOfDate).getTime() : Date.now()
  const yearsElapsed = (msTarget - msPurchase) / (365.25 * 24 * 3600 * 1000)
  if (profile.method === 'STRAIGHT_LINE') {
    return Math.max(pp - pp * parseFloat(profile.annual_rate) * yearsElapsed, salvage)
  }
  if (profile.method === 'DECLINING_BALANCE') {
    return Math.max(pp * Math.pow(1 - parseFloat(profile.annual_rate), yearsElapsed), salvage)
  }
  if (profile.method === 'INSURANCE_STEP') {
    const grace = parseFloat(profile.grace_period_years ?? 0)
    if (yearsElapsed <= grace) return pp
    return Math.max(pp - pp * parseFloat(profile.annual_rate) * (yearsElapsed - grace), salvage)
  }
  return null
}

const depProfile     = computed(() => uniqueInstRaw.value?.effective_depreciation_profile ?? null)
const depPurchasePrice = computed(() => parseFloat(uniqueInstRaw.value?.purchase_price) || null)
const depPurchaseDate  = computed(() => uniqueInstRaw.value?.purchase_date ?? null)
const depBookValue     = computed(() => {
  const bv = uniqueInstRaw.value?.book_value
  return bv !== null && bv !== undefined ? parseFloat(bv) : null
})
const depPct = computed(() => {
  if (depPurchasePrice.value && depBookValue.value !== null)
    return Math.max(0, Math.min(100, (1 - depBookValue.value / depPurchasePrice.value) * 100))
  return null
})
const depProfileLevel = computed(() => {
  if (!depProfile.value) return null
  if (uniqueInstRaw.value?.depreciation_profile) return t('itemView.instanceOverride')
  const itemProfileId = uniqueInstRaw.value?.item?.depreciation_profile
  if (itemProfileId) return t('itemView.itemDefault')
  return t('itemView.tenantDefault')
})
const METHOD_LABELS = computed(() => ({
  STRAIGHT_LINE: t('itemView.methodStraightLineDisplay'),
  DECLINING_BALANCE: t('itemView.methodDecliningBalanceDisplay'),
  INSURANCE_STEP: t('itemView.methodInsuranceStepDisplay'),
}))
const depSchedule = computed(() => {
  const pp = depPurchasePrice.value
  const pd = depPurchaseDate.value
  const profile = depProfile.value
  if (!pp || !pd || !profile) return []
  const purchaseYear = new Date(pd).getFullYear()
  const currentYear  = new Date().getFullYear()
  const lifeYears    = Math.ceil(1 / parseFloat(profile.annual_rate || 1)) + 2
  const endYear      = Math.max(currentYear + 3, purchaseYear + lifeYears)
  const rows = []
  for (let yr = purchaseYear; yr <= endYear; yr++) {
    const atStart = calcBookValue(pp, pd, profile, `${yr}-01-01`)
    const atEnd   = calcBookValue(pp, pd, profile, `${yr}-12-31`)
    if (atStart === null) break
    rows.push({ year: yr, value: atStart, depreciation: atStart - (atEnd ?? atStart), isCurrent: yr === currentYear })
    if (atEnd !== null && atEnd <= pp * parseFloat(profile.salvage_value_percentage ?? 0) + 0.01) break
  }
  return rows
})
function fmtCurrency(val) {
  if (val === null || val === undefined) return '—'
  return new Intl.NumberFormat('nb-NO', { style: 'currency', currency: 'NOK', maximumFractionDigits: 0 }).format(val)
}

// ── fleet availability data ───────────────────────────────────────────
const fleetCheckouts  = ref([])
const fleetTimelineSegments = ref([])
const fleetLoading    = ref(false)

// ── operational data (types 1, 3, 4) ──────────────────────────────────
const activeCheckouts  = ref([])
const historyTruncated = ref(false)
const projectPlans     = ref([])
const plansPage        = ref(1)
const plansPageSize    = 5
const plansTotalCount  = ref(0)
const plansLoading     = ref(false)
const plansFilter      = ref('upcoming')
const projectNameMap   = ref({})
const plansTotalPages  = computed(() => Math.ceil(plansTotalCount.value / plansPageSize))

const planFilters = computed(() => [
  { v: 'upcoming', l: t('itemView.planFilterUpcoming') },
  { v: 'active', l: t('itemView.planFilterActive') },
  { v: 'past', l: t('itemView.planFilterPast') },
])
const currentPlanFilterLabel = computed(() => planFilters.value.find(f => f.v === plansFilter.value)?.l ?? plansFilter.value)

// ── maintenance schedule modal ────────────────────────────────────────
const showAddScheduleModal = ref(false)
const addScheduleSearch    = ref('')
const addScheduleResults   = ref([])
const addScheduleLoading   = ref(false)
const addingScheduleId     = ref(null)

function goToParent() {
  if (!parentInstance.value) return
  const p = parentInstance.value
  if (p.item?.item_type === 2) {
    router.push(`/instance/${p.id}`)
  } else if (p.item?.id) {
    router.push(`/item/${p.item.id}`)
  }
}

async function loadInstances() {
  if (isNew.value) return
  instancesLoading.value = true
  try {
    const res = await getItemInstances(itemId.value)
    instances.value = res.results ?? res ?? []
    // pre-populate inline form for unique and quantity items
    if ((form.value.item_type === 1 || form.value.item_type === 3 || form.value.item_type === 4) && instances.value.length) {
      const u = instances.value[0]
      uniqueInstId.value  = u.id
      uniqueInstRaw.value = u
      const parentId = typeof u.parent === 'object' ? u.parent?.id : u.parent
      if (parentId) {
        getItemInstance(parentId).then(p => { parentInstance.value = p }).catch(() => {})
      } else {
        parentInstance.value = null
      }
      uniqueInst.value = {
        assetTag:           u.assetTag          ?? u.asset_tag ?? '',
        serial_number:      u.serial_number     ?? '',
        area:               u.area?.id          ?? u.area ?? '',
        status:             u.status            ?? 1,
        comment:            u.comment           ?? '',
        purchase_price:     u.purchase_price    ?? '',
        available_quantity: u.available_quantity ?? '',
        availability_net_quantity: u.consumable_net_available ?? null,
        restock_threshold:  u.restock_threshold  ?? '',
        restock_quantity:   u.restock_quantity   ?? '',
        in_downtime:        u.in_downtime       ?? false,
        downtime_reason:    u.downtime_reason   ?? '',
        supplier:           (typeof u.supplier === 'object' ? u.supplier?.id : u.supplier) ?? '',
        depreciation_profile: (typeof u.depreciation_profile === 'object' ? u.depreciation_profile?.id : u.depreciation_profile) ?? '',
      }
    }
  } catch {
    instances.value = []
  } finally {
    instancesLoading.value = false
  }
  // Load checkout history for availability panels (non-blocking)
  loadFleetData()
}

async function loadFleetData() {
  if (isNew.value) return
  fleetLoading.value = true
  try {
    if ((form.value.item_type === 1 || form.value.item_type === 3 || form.value.item_type === 4) && uniqueInstId.value) {
      const availabilityTimelineRequest = form.value.item_type === 1
        ? getItemMetadataTimeline(uniqueInstId.value)
        : getItemFleetTimeline(itemId.value)
      const [historyRes, checkoutsRes, timelineRes] = await Promise.allSettled([
        getCheckoutHistoryForInstance(uniqueInstId.value, 30),
        getActiveCheckouts({ item_metadata: uniqueInstId.value }),
        availabilityTimelineRequest,
      ])
      if (historyRes.status === 'fulfilled') {
        const all = historyRes.value?.results ?? historyRes.value ?? []
        historyTruncated.value = (historyRes.value?.count ?? all.length) > 30
        fleetCheckouts.value = all
      }
      if (checkoutsRes.status === 'fulfilled') {
        activeCheckouts.value = checkoutsRes.value?.results ?? checkoutsRes.value ?? []
      }
      if (timelineRes.status === 'fulfilled') {
        if (form.value.item_type === 1) {
          fleetTimelineSegments.value = timelineRes.value?.segments ?? []
        } else {
          const row = (timelineRes.value ?? []).find(result => result.id === Number(uniqueInstId.value))
          fleetTimelineSegments.value = row?.segments?.map(segment => ({ ...segment, asset_tag: row.asset_tag })) ?? []
          uniqueInst.value.availability_net_quantity = row?.net_available ?? uniqueInst.value.availability_net_quantity
        }
      }
      loadPlans(1)
    } else if (form.value.item_type === 2 && instances.value.length) {
      const rows = await getItemFleetTimeline(itemId.value)
      fleetTimelineSegments.value = (rows ?? []).flatMap(row =>
        (row.segments ?? []).map(segment => ({ ...segment, asset_tag: row.asset_tag })),
      )
    }
  } catch {
    fleetCheckouts.value = []
    fleetTimelineSegments.value = []
  } finally {
    fleetLoading.value = false
  }
}

function toggleAddForm() {
  showAddForm.value = !showAddForm.value
  if (showAddForm.value) {
    newInst.value      = { assetTag: '', serial_number: '', area: '', status: 1, comment: '', purchase_price: '', available_quantity: '' }
    addInstError.value = null
  }
}

function cancelAddForm() { showAddForm.value = false }

async function submitAddInstance() {
  if (!newInstValid.value || addingInst.value) return
  addingInst.value   = true
  addInstError.value = null
  try {
    const payload = {
      item:   parseInt(itemId.value, 10),
      area:   parseInt(newInst.value.area, 10),
      status: newInst.value.status,
    }
    if (newInst.value.assetTag)              payload.assetTag          = newInst.value.assetTag
    if (newInst.value.serial_number)         payload.serial_number     = newInst.value.serial_number
    if (newInst.value.comment)               payload.comment           = newInst.value.comment
    if (newInst.value.purchase_price !== '') payload.purchase_price    = parseFloat(newInst.value.purchase_price)
    if (newInst.value.supplier !== '')       payload.supplier          = parseInt(newInst.value.supplier, 10)
    if (newInst.value.depreciation_profile !== '') payload.depreciation_profile = parseInt(newInst.value.depreciation_profile, 10)
    if (form.value.item_type === 3 && newInst.value.available_quantity !== '')
      payload.available_quantity = parseInt(newInst.value.available_quantity, 10)

    await createItemInstance(payload)
    toast.success(t('itemView.instanceCreated'))
    showAddForm.value = false
    await loadInstances()
  } catch (e) {
  addInstError.value = e?.data?.detail ?? e?.data?.area?.[0] ?? e?.message ?? t('itemView.instanceCreateFailed')
  } finally {
    addingInst.value = false
  }
}

function goToInstance(inst) {
  router.push(`/instance/${inst.id}`)
}

// ── status helpers ────────────────────────────────────────────────────
const statusLabelMap = computed(() => ({
  1: t('instanceStatus.inService'),
  2: t('instanceStatus.defective'),
  3: t('instanceStatus.retired'),
}))
function instStatusLabel(inst) {
  if (inst.status === 1 && inst.is_checked_out) return t('checkout.checkedOutBadge')
  return statusLabelMap.value[inst.status] ?? t('itemTypes.unknown')
}
function statusClass(s) {
  return { 1: 'badge badge-success', 2: 'badge badge-danger', 3: 'badge badge-muted' }[s] ?? 'badge badge-muted'
}
function instStatusClass(inst) {
  if (inst.status === 1 && inst.is_checked_out) return 'badge badge-accent'
  return statusClass(inst.status)
}

// ── save ──────────────────────────────────────────────────────────────
function resetForm() {
  form.value = { name: '', category: '', item_type: '', description: '', rental_price_per_day: '', wiki_page: '', depreciation_profile: '', is_container: false, available_in_portal: false, contract_addition_type: 'none', contract_addition_html: '' }
  additionFile.value = null
  additionFilename.value = ''
  uniqueInst.value = { assetTag: '', serial_number: '', area: '', status: 1, comment: '', purchase_price: '', available_quantity: '', availability_net_quantity: null, restock_threshold: '', restock_quantity: '', supplier: '', depreciation_profile: '' }
  newInst.value = { assetTag: '', serial_number: '', area: '', status: 1, comment: '', purchase_price: '', available_quantity: '', supplier: '', depreciation_profile: '' }
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function save(addNew = false, andClose = false) {
  if (!formValid.value || saving.value) return
  saving.value = true
  try {
    const payload = {
      name:                 form.value.name.trim(),
      category:             parseInt(form.value.category, 10),
      item_type:            parseInt(form.value.item_type, 10),
      description:          form.value.description || null,
      rental_price_per_day: form.value.rental_price_per_day !== '' ? form.value.rental_price_per_day : null,
      wiki_page:            form.value.wiki_page !== '' ? parseInt(form.value.wiki_page, 10) : null,
      depreciation_profile: form.value.depreciation_profile !== '' ? parseInt(form.value.depreciation_profile, 10) : null,
      is_container: form.value.is_container,
      available_in_portal: form.value.available_in_portal,
      contract_addition_type: form.value.contract_addition_type,
      contract_addition_html: form.value.contract_addition_html,
      ...(additionFile.value ? { contract_addition_pdf: additionFile.value } : {}),
    }
    if (isNew.value) {
      const created = await createItem(payload)
      // Create the first instance so the item is visible in the inventory
      const instRef = (form.value.item_type === 2) ? newInst.value : uniqueInst.value
      const instPayload = {
        item:   created.id,
        area:   parseInt(instRef.area, 10),
        status: instRef.status,
      }
      if (instRef.assetTag)              instPayload.assetTag           = instRef.assetTag
      if (instRef.serial_number)         instPayload.serial_number      = instRef.serial_number
      if (instRef.comment)               instPayload.comment            = instRef.comment
      if (instRef.purchase_price !== '') instPayload.purchase_price     = parseFloat(instRef.purchase_price)
      if (instRef.supplier !== '')       instPayload.supplier           = parseInt(instRef.supplier, 10)
      if (instRef.depreciation_profile !== '') instPayload.depreciation_profile = parseInt(instRef.depreciation_profile, 10)
      if (form.value.item_type === 3 && instRef.available_quantity !== '')
        instPayload.available_quantity = parseInt(instRef.available_quantity, 10)
      if (form.value.item_type === 4) {
        if (instRef.available_quantity !== '') instPayload.available_quantity = parseInt(instRef.available_quantity, 10)
        if (instRef.restock_threshold !== '')  instPayload.restock_threshold  = parseInt(instRef.restock_threshold, 10)
        if (instRef.restock_quantity !== '')   instPayload.restock_quantity   = parseInt(instRef.restock_quantity, 10)
      }
      await createItemInstance(instPayload)
      toast.success(t('itemView.itemTypeCreated'))
      store.fetchItems(true)
      if (addNew) {
        resetForm()
      } else {
        router.replace(`/item/${created.id}`)
      }
    } else {
      const saved = await putItem({ ...payload, id: parseInt(itemId.value, 10) })
      form.value.contract_addition_html = saved.contract_addition_html ?? ''
      additionFilename.value = saved.contract_addition_filename ?? ''
      additionFile.value = null
      // For unique/quantity/consumable items, also save the inline instance
      if ((form.value.item_type === 1 || form.value.item_type === 3 || form.value.item_type === 4) && uniqueInst.value.area !== '') {
        const instPayload = {
          item:   parseInt(itemId.value, 10),
          area:   parseInt(uniqueInst.value.area, 10),
          status: uniqueInst.value.status,
        }
        if (uniqueInst.value.assetTag)                instPayload.assetTag           = uniqueInst.value.assetTag
        if (uniqueInst.value.serial_number)           instPayload.serial_number      = uniqueInst.value.serial_number
        if (uniqueInst.value.comment)                 instPayload.comment            = uniqueInst.value.comment
        if (uniqueInst.value.purchase_price !== '')   instPayload.purchase_price     = parseFloat(uniqueInst.value.purchase_price)
        instPayload.supplier = uniqueInst.value.supplier !== '' ? parseInt(uniqueInst.value.supplier, 10) : null
        instPayload.depreciation_profile = uniqueInst.value.depreciation_profile !== '' ? parseInt(uniqueInst.value.depreciation_profile, 10) : null
        if (form.value.item_type === 3 && uniqueInst.value.available_quantity !== '')
          instPayload.available_quantity = parseInt(uniqueInst.value.available_quantity, 10)
        if (form.value.item_type === 4) {
          if (uniqueInst.value.available_quantity !== '') instPayload.available_quantity = parseInt(uniqueInst.value.available_quantity, 10)
          if (uniqueInst.value.restock_threshold !== '')  instPayload.restock_threshold  = parseInt(uniqueInst.value.restock_threshold, 10)
          if (uniqueInst.value.restock_quantity !== '')   instPayload.restock_quantity   = parseInt(uniqueInst.value.restock_quantity, 10)
        }
        if (uniqueInstId.value) {
          await updateItemInstance(uniqueInstId.value, instPayload)
        } else {
          const created = await createItemInstance(instPayload)
          uniqueInstId.value = created.id
        }
      }
      store.fetchItems(true)
      toast.success(t('itemView.itemTypeSaved'))
      if (andClose) router.back()
    }
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.data?.contract_addition_html?.[0] ?? e?.data?.contract_addition_pdf?.[0] ?? e?.data?.name?.[0] ?? e?.message ?? t('itemView.saveFailed'))
  } finally {
    saving.value = false
  }
}

// ── delete ────────────────────────────────────────────────────────────
function onDelete() { showDeleteConfirm.value = true }

async function confirmDelete() {
  deleting.value = true
  try {
    await deleteItem(itemId.value)
    store.fetchItems(true)
    toast.success(t('itemView.itemTypeDeleted'))
    router.replace('/items')
  } catch {
    toast.error(t('itemView.itemTypeDeleteFailed'))
    deleting.value = false
    showDeleteConfirm.value = false
  }
}

// ── category creation ─────────────────────────────────────────────────
function toggleNewCategoryForm() {
  showNewCategoryForm.value = !showNewCategoryForm.value
  if (showNewCategoryForm.value) {
    newCategoryForm.value = { name: '', parent: null }
  }
}

async function saveNewCategory() {
  if (!newCategoryForm.value.name.trim()) {
    toast.error(t('itemView.categoryNameRequired'))
    return
  }
  
  savingCategory.value = true
  try {
    const response = await createCategory(newCategoryForm.value)
    await store.fetchCategories(true)
    form.value.category = response.id
    toast.success(t('itemView.categoryCreated'))
    showNewCategoryForm.value = false
    newCategoryForm.value = { name: '', parent: null }
  } catch (error) {
    toast.error(t('itemView.categoryCreateFailed'))
    console.error(error)
  } finally {
    savingCategory.value = false
  }
}

// ── area creation ─────────────────────────────────────────────────────
async function toggleNewAreaForm() {
  showNewAreaForm.value = !showNewAreaForm.value
  if (showNewAreaForm.value) {
    const defaultLocation = store.currentLocation?.id || ''
    const defaultWarehouse = store.currentWarehouse?.id || ''
    // Fetch warehouses before setting form so the watch doesn't clobber the default warehouse
    if (defaultLocation) {
      newAreaFormWarehouses.value = await getWarehouses({ location: defaultLocation }).then(r => r.results ?? r ?? []).catch(() => [])
    } else {
      newAreaFormWarehouses.value = []
    }
    skipAreaLocationWatch = true
    newAreaForm.value = { name: '', location: defaultLocation, warehouse: defaultWarehouse }
    await nextTick()
    skipAreaLocationWatch = false
  }
}

watch(() => newAreaForm.value.location, async (locationId) => {
  if (skipAreaLocationWatch) return
  newAreaForm.value.warehouse = ''
  if (locationId) {
    newAreaFormWarehouses.value = await getWarehouses({ location: locationId }).then(r => r.results ?? r ?? []).catch(() => [])
  } else {
    newAreaFormWarehouses.value = []
  }
})

async function saveNewArea() {
  if (!newAreaForm.value.name.trim()) {
    toast.error(t('itemView.areaNameRequired'))
    return
  }
  
  savingArea.value = true
  try {
    const payload = { name: newAreaForm.value.name }
    if (newAreaForm.value.warehouse) {
      payload.warehouse = Number(newAreaForm.value.warehouse)
    }
    const response = await createArea(payload)
    // Reload areas list
    const areaParams = store.currentWarehouse?.id ? { warehouse: store.currentWarehouse.id } : {}
    const areaList = await getAreas(areaParams).then(r => r.results ?? r ?? []).catch(() => [])
    areas.value = areaList
    // Auto-select the newly created area in both forms
    uniqueInst.value.area = response.id
    newInst.value.area = response.id
    toast.success(t('itemView.areaCreated'))
    showNewAreaForm.value = false
    newAreaForm.value = { name: '', warehouse: '' }
  } catch (error) {
    toast.error(t('itemView.areaCreateFailed'))
    console.error(error)
  } finally {
    savingArea.value = false
  }
}

// ── supplier creation ─────────────────────────────────────────────────
function toggleNewSupplierForm() {
  showNewSupplierForm.value = !showNewSupplierForm.value
  if (showNewSupplierForm.value) {
    newSupplierForm.value = { name: '', contact_person: '' }
  }
}

async function saveNewSupplier() {
  if (!newSupplierForm.value.name.trim()) {
    toast.error(t('itemView.supplierNameRequired'))
    return
  }
  savingSupplier.value = true
  try {
    const payload = { name: newSupplierForm.value.name.trim() }
    if (newSupplierForm.value.contact_person.trim()) payload.contact_person = newSupplierForm.value.contact_person.trim()
    const response = await createSupplier(payload)
    suppliers.value = await getSuppliers().then(r => r.results ?? r ?? []).catch(() => suppliers.value)
    uniqueInst.value.supplier = response.id
    newInst.value.supplier = response.id
    toast.success(t('itemView.supplierCreated'))
    showNewSupplierForm.value = false
    newSupplierForm.value = { name: '', contact_person: '' }
  } catch {
    toast.error(t('itemView.supplierCreateFailed'))
  } finally {
    savingSupplier.value = false
  }
}

// ── depreciation profile creation ────────────────────────────────────
function toggleNewDepreciationProfileForm() {
  showNewDepreciationProfileForm.value = !showNewDepreciationProfileForm.value
  if (showNewDepreciationProfileForm.value) {
    newDepreciationProfileForm.value = { name: '', method: 'STRAIGHT_LINE', annual_rate: '', grace_period_years: '' }
  }
}

async function saveNewDepreciationProfile() {
  if (!newDepreciationProfileForm.value.name.trim() || !newDepreciationProfileForm.value.annual_rate) {
    toast.error(t('itemView.depProfileRequiredFields'))
    return
  }
  savingDepreciationProfile.value = true
  try {
    const payload = {
      name: newDepreciationProfileForm.value.name.trim(),
      method: newDepreciationProfileForm.value.method,
      annual_rate: newDepreciationProfileForm.value.annual_rate,
    }
    if (auth.currentTenant?.id) payload.tenant = auth.currentTenant.id
    if (newDepreciationProfileForm.value.grace_period_years !== '') {
      payload.grace_period_years = parseInt(newDepreciationProfileForm.value.grace_period_years, 10)
    }
    const response = await createDepreciationProfile(payload)
    depreciationProfiles.value = await getDepreciationProfiles().then(r => r.results ?? r ?? []).catch(() => depreciationProfiles.value)
    uniqueInst.value.depreciation_profile = response.id
    newInst.value.depreciation_profile = response.id
    toast.success(t('itemView.depProfileCreated'))
    showNewDepreciationProfileForm.value = false
    newDepreciationProfileForm.value = { name: '', method: 'STRAIGHT_LINE', annual_rate: '', grace_period_years: '' }
  } catch {
    toast.error(t('itemView.depProfileCreateFailed'))
  } finally {
    savingDepreciationProfile.value = false
  }
}

// ── maintenance downtime ─────────────────────────────────────────────
function openMarkMaintenance(instId) {
  downtimeTargetId.value   = instId
  markDowntimeReason.value = ''
  showMarkDowntimeModal.value = true
}

function openReturnService(instId) {
  downtimeTargetId.value = instId
  showReturnServiceModal.value = true
}

async function confirmMarkMaintenance() {
  togglingDowntime.value = true
  try {
    await patchItemMetadataDowntime(downtimeTargetId.value, true, markDowntimeReason.value.trim())
    if (downtimeTargetId.value === uniqueInstId.value) {
      uniqueInst.value.in_downtime     = true
      uniqueInst.value.downtime_reason = markDowntimeReason.value.trim()
    } else {
      const inst = instances.value.find(i => i.id === downtimeTargetId.value)
      if (inst) { inst.in_downtime = true; inst.downtime_reason = markDowntimeReason.value.trim() }
    }
    toast.success(t('itemView.markedDowntime'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('itemView.downtimeFailed'))
  } finally {
    togglingDowntime.value = false
    showMarkDowntimeModal.value = false
  }
}

async function confirmReturnService() {
  togglingDowntime.value = true
  try {
    await patchItemMetadataDowntime(downtimeTargetId.value, false, '')
    if (downtimeTargetId.value === uniqueInstId.value) {
      uniqueInst.value.in_downtime     = false
      uniqueInst.value.downtime_reason = ''
    } else {
      const inst = instances.value.find(i => i.id === downtimeTargetId.value)
      if (inst) { inst.in_downtime = false; inst.downtime_reason = '' }
    }
    toast.success(t('itemView.returnedToService'))
  } catch (e) {
    toast.error(e?.data?.detail ?? e?.message ?? t('itemView.downtimeFailed'))
  } finally {
    togglingDowntime.value = false
    showReturnServiceModal.value = false
  }
}

async function loadPlans(page = 1) {
  if (!uniqueInstId.value) return
  plansLoading.value = true
  plansPage.value = page
  try {
    const res = await getProjectPlansForInstance(uniqueInstId.value, { page, pageSize: plansPageSize, timing: plansFilter.value })
    const allPlans = res.results ?? res ?? []
    plansTotalCount.value = res.count ?? allPlans.length
    projectPlans.value = allPlans
    const unknownIds = [...new Set(allPlans.map(p => p.project).filter(id => id && !projectNameMap.value[id]))]
    if (unknownIds.length) {
      const results = await Promise.allSettled(unknownIds.map(id => getProject(id)))
      unknownIds.forEach((id, i) => {
        if (results[i].status === 'fulfilled') {
          projectNameMap.value[id] = results[i].value?.name ?? t('ui.projectNumber', { id: id })
        }
      })
    }
  } catch {
    // non-critical
  } finally {
    plansLoading.value = false
  }
}

function fmtDatetime(dt) {
  if (!dt) return ''
  return new Date(dt).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

function openAddScheduleModal() {
  addScheduleSearch.value  = ''
  addScheduleResults.value = []
  showAddScheduleModal.value = true
}
function closeAddScheduleModal() { showAddScheduleModal.value = false }
let scheduleSearchTimer = null
function onAddScheduleSearch() {
  clearTimeout(scheduleSearchTimer)
  const q = addScheduleSearch.value.trim()
  if (!q) { addScheduleResults.value = []; addScheduleLoading.value = false; return }
  addScheduleLoading.value = true
  scheduleSearchTimer = setTimeout(async () => {
    try {
      const res = await getMaintenanceSchedules({ search: q, page_size: 10 })
      addScheduleResults.value = res.results ?? res ?? []
    } catch {
      addScheduleResults.value = []
    } finally {
      addScheduleLoading.value = false
    }
  }, 250)
}

async function addToSchedule(schedule) {
  if (addingScheduleId.value || !uniqueInstId.value) return
  addingScheduleId.value = schedule.id
  try {
    const existing = schedule.instances ?? []
    const updatedInstances = [...new Set([...existing.map(i => (typeof i === 'object' ? i.id : i)), uniqueInstId.value])]
    await updateMaintenanceSchedule(schedule.id, { instances: updatedInstances })
    toast.success(t('itemView.addedToScheduleToast', { title: schedule.title }))
    closeAddScheduleModal()
  } catch {
    toast.error(t('itemView.addToScheduleFailed'))
  } finally {
    addingScheduleId.value = null
  }
}

// ── lifecycle ─────────────────────────────────────────────────────────
onMounted(load)
watch(() => store.categories, (cats) => { if (cats.length) categories.value = cats }, { immediate: true })
watch(itemId, (newId, oldId) => { if (newId && newId !== oldId && newId !== 'new') load() })
</script>
